import {useEffect, useRef, useState, type PointerEvent} from 'react';
type Point={x:number;y:number};
type View=Point&{scale:number};
const initial:View={scale:1,x:0,y:0};
const limit=(value:number,min:number,max:number)=>Math.max(min,Math.min(max,value));

/** Local image gestures; navigation is allowed only after a one-finger swipe at fit size. */
export function TouchImagePreview({src,name,onError,onNavigate}:{src:string;name:string;onError:()=>void;onNavigate:(direction:number)=>void}){
  const host=useRef<HTMLDivElement>(null),img=useRef<HTMLImageElement>(null);
  const current=useRef<View>(initial),points=useRef(new Map<number,Point>());
  const swipe=useRef<{start:Point;eligible:boolean;canTap:boolean}|null>(null);
  const tap=useRef<{point:Point;time:number}|null>(null);
  const [view,setView]=useState(initial);
  const apply=(next:View)=>{
    const box=host.current?.getBoundingClientRect(),image=img.current;if(!box||!box.width||!box.height)return;
    const ratio=image?.naturalWidth&&image.naturalHeight?image.naturalWidth/image.naturalHeight:box.width/box.height;
    const width=Math.min(box.width,box.height*ratio),height=width/ratio,scale=limit(next.scale,1,8);
    const dx=Math.max(0,(width*scale-box.width)/2),dy=Math.max(0,(height*scale-box.height)/2);
    const value={scale,x:limit(next.x,-dx,dx),y:limit(next.y,-dy,dy)};
    current.current=value;setView(value);
  };
  useEffect(()=>{
    const node=host.current;if(!node)return;
    // Resize resets the framing, including device orientation changes.
    const observer=new ResizeObserver(()=>{current.current=initial;setView(initial);points.current.clear();swipe.current=null;tap.current=null;});
    observer.observe(node);return()=>observer.disconnect();
  },[]);
  const zoomAt=(point:Point)=>{
    if(current.current.scale>1.01){apply(initial);return;}
    const box=host.current!.getBoundingClientRect();
    apply({scale:2.5,x:-(point.x-box.left-box.width/2)*1.5,y:-(point.y-box.top-box.height/2)*1.5});
  };
  const down=(e:PointerEvent<HTMLDivElement>)=>{
    if(e.pointerType==='mouse'&&e.button!==0)return;
    e.preventDefault();const point={x:e.clientX,y:e.clientY};
    points.current.set(e.pointerId,point);e.currentTarget.setPointerCapture(e.pointerId);
    if(points.current.size===1)swipe.current={start:point,eligible:current.current.scale<=1.01,canTap:true};
    else{if(swipe.current){swipe.current.eligible=false;swipe.current.canTap=false;}tap.current=null;}
  };
  const move=(e:PointerEvent<HTMLDivElement>)=>{
    const previous=points.current.get(e.pointerId);if(!previous)return;
    const before=[...points.current.values()];points.current.set(e.pointerId,{x:e.clientX,y:e.clientY});const old=current.current;
    if(swipe.current&&Math.hypot(e.clientX-swipe.current.start.x,e.clientY-swipe.current.start.y)>12)swipe.current.canTap=false;
    if(points.current.size===2){
      const after=[...points.current.values()],distance=(p:Point[])=>Math.hypot(p[0].x-p[1].x,p[0].y-p[1].y),mid=(p:Point[])=>({x:(p[0].x+p[1].x)/2,y:(p[0].y+p[1].y)/2});
      const a=mid(before),b=mid(after),box=e.currentTarget.getBoundingClientRect();
      const scale=limit(old.scale*distance(after)/Math.max(1,distance(before)),1,8),k=scale/old.scale;
      const x=a.x-box.left-box.width/2,y=a.y-box.top-box.height/2;
      apply({scale,x:x-(x-old.x)*k+b.x-a.x,y:y-(y-old.y)*k+b.y-a.y});
    }else if(points.current.size===1&&old.scale>1.01)apply({...old,x:old.x+e.clientX-previous.x,y:old.y+e.clientY-previous.y});
  };
  const finish=(e:PointerEvent<HTMLDivElement>,cancel=false)=>{
    if(!points.current.has(e.pointerId))return;
    points.current.delete(e.pointerId);
    const gesture=swipe.current;
    if(cancel){swipe.current=null;tap.current=null;}
    else if(!points.current.size&&gesture){
      const dx=e.clientX-gesture.start.x,dy=e.clientY-gesture.start.y;
      if(gesture.eligible&&current.current.scale<=1.01&&Math.abs(dx)>60&&Math.abs(dx)>Math.abs(dy)*1.4){tap.current=null;onNavigate(dx<0?1:-1);}
      else if(gesture.canTap&&Math.hypot(dx,dy)<12){
        const point={x:e.clientX,y:e.clientY},now=Date.now(),last=tap.current;
        if(last&&now-last.time<330&&Math.hypot(point.x-last.point.x,point.y-last.point.y)<32){tap.current=null;zoomAt(point);}
        else tap.current={point,time:now};
      }else tap.current=null;
      swipe.current=null;
    }
    if(e.currentTarget.hasPointerCapture(e.pointerId))e.currentTarget.releasePointerCapture(e.pointerId);
  };
  return <div ref={host} className="sx-touch-image" role="group" aria-label="Изображение: щипок — масштаб, свайп — следующий файл, двойной тап — увеличить или уместить" onPointerDown={down} onPointerMove={move} onPointerUp={e=>finish(e)} onPointerCancel={e=>finish(e,true)} onLostPointerCapture={e=>finish(e,true)}>
    <img ref={img} src={src} alt={name} draggable={false} onError={onError} style={{transform:`translate(${view.x}px,${view.y}px) scale(${view.scale})`}}/>
  </div>;
}
